/* eslint-disable max-lines */
import config from "appConfig";
import { navigateToLogin } from "../history";
import ReactGA from "react-ga4";
import {
  formatNumber,
  formatDate,
  isAllowedValue,
  buildBaseUrl,
  isObject,
  isIE,
  isEdge
} from "./utils";
import { showError } from "./notification";
import {
  DetailValueType,
  ApiCacheKey,
  LocalStorageKey,
  emptyUUID,
  PhoneInterface
} from "../models/enums";
import * as localStorage from "./localStorage";
import { removeCache, getCachedResult, CACHE_KEY_SEPARATOR } from "./apiCache";
import { getCategoryConfig } from "../dataConfig/categoryConfig";
import { getCookie } from "../utils/cookies";
import { RequestsI } from "./apiTypes";
import { PassedAdI } from "../typings/routesTypes";
import { CommonOptionI, DetailI } from "../dataConfig/dataConfigTypes";
import moment from "moment";
import {
  mapCategoryToEn,
  mapCategoryToHr,
  mapModulesAndCategories,
  mapModuleToEn,
  mapModuleToHr
} from "../routes/LanguageMapper";
import { notification } from "./index";
import {
  getChatsRequest,
  getChatsListResponse,
  getChatRequest,
  sendMessageRequest,
  unreadCountResponse
} from "../typings/interfaces";
import { CHAT_REPORT_REASON_ENUM } from "../containers/ChatsPage/constants/constants";

const defaultOptions = (isRemote: boolean): RequestInit => {
  return {
    headers: isRemote ? {} : {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  }
};

const parseQueryString = (object: object) => {
  let result = "";

  isObject(object) &&
    Object.keys(object).forEach(key => {
      const value = object[key];

      if (isAllowedValue(value)) {
        if (value instanceof Array) {
          value.forEach(v => {
            result += `&${key}=${v}`;
          });
        } else {
          result += `&${key}=${value}`;
        }
      }
    });

  return result.length === 0 ? "" : `?${result.slice(1, result.length)}`;
};

const resolveEndpoint = (path: string) => {
  if (isEdge) {
    defaultOptions.credentials = "include";
  }

  return buildBaseUrl(`${path}`);
};

export const handleUnauthorizedError = () => {
  showError("Molimo, prijavite se za nastavak");

  navigateToLogin();
};

export const handleCommonError = async (response: any) => {
  let error: any = {};
  try {
    error = await response.json();
  } catch (ex) {
    if (response.status) {
      showError("Greška: {0}".replace("{0}", String(response.status)));
    } else {
      showError("Greška: {0}".replace("{0}", String(1000001)));
    }
  }

  if (error.code && error.code != 401) {
    showErrorByCode(error.code);
  }
};

export const handleBusinessError = async (response: any) => {
  let error: any = {};
  try {
    error = await response.json();
  } catch (ex) {
    // fix eslint
  }

  switch (error.code) {
    case 1003:
      showError("Ova e-mail adresa je već zauzeta.");
      break;
    case 1019:
      notification.showError("Uneseni OIB već postoji u našoj bazi. Provjerite uneseni broj, a po potrebi nas možete kontaktirati na 091/6089171 ili oglasi@index.hr.");
      break;
    case 1020:
      notification.showError("Uneseni OIB već postoji u našoj bazi. Provjerite uneseni broj, a po potrebi nas možete kontaktirati na 091/6089171 ili oglasi@index.hr.");
      break;
    case 1021:
      showError("Vaš račun je blokiran. Za više informacija nas kontaktirajte na oglasi@index.hr ili 091/6089171.");
      break;
    case 1022:
      showError("Korisnik nije verificiran. Provjerite svoju adresu e-pošte.");
      break;
    case 1023:
      showError("Unijeli ste nedozvoljeni podatak. Kontaktirajte nas na 091/6089171 ili na oglasi@index.hr.")
      break;
    case 1024:
      showError("Unijeli ste nedozvoljene riječi. Promijenite tekst ili nas kontaktirajte na 091/6089171 ili na oglasi@index.hr.")
      break;
    case 1025:
      showError("Nije dozvoljen unos eksternih linkova u oglase. Promijenite unjeti tekst ili nas kontaktirajte na 091/6089171 ili na oglasi@index.hr.")
      break;
    case 1026:
      showError("Naslov oglasa je obvezan.")
      break;
    case 1027:
      showError("Dosegli ste maksimalan broj spremljenih pretraga.")
      break;
    case 1028:
      showError("Ograničenje u broju parametara za sačuvane pretrage je 15.")
      break;

    // CDN error codes (100000 - 999999)
    case 100000:
      showError("Trenutno nije moguće dodati oglas iz vaše države. Molimo nazovite nas na 091/6089171 ili pišite na oglasi@index.hr")
      break;

    // Default error
    default:
      notification.showError("Uneseni podaci nisu ispravni, molimo provjerite unesene podatke");
      break;
  }
}

// eslint-disable-next-line complexity
export const showErrorByCode = (errorCode: number) => {
  switch (errorCode) {
    case 1000:
      showError("Nešto je pošlo po zlu, molimo pokušajte ponovo kasnije!");
      break;

    case 1002:
      ReactGA.event({
        category: "form error",
        action: "submit",
        label: "Zauzeto korisničko ime"
      });
      showError("Ovo korisničko ime je već zauzeto.");
      break;

    case 1003:
      showError("Ova e-mail adresa je već zauzeta.");
      break;

    case 1004:
      showError("Ispunite sva obavezna polja.");
      break;

    case 1007:
      showError("Opis ne može biti dulji od 200 znakova");
      break;

    case 1010:
      showError("Nevažeći e-mail.");
      break;

    case 1011:
      showError("Oglas nije pronađen.");
      break;

    case 1013:
      showError("Korisnik nije pronađen.");
      break;

    case 1012:
      showError("Korisnik nije pravna osoba.");
      break;

    case 1016:
      showError("Trenutna zaporka je pogrešna");
      break;

    case 1017:
      showError("Vaš račun je blokiran. Za više informacija nas kontaktirajte na oglasi@index.hr ili 091/6089171.");
      break;

    case 1018:
      showError("E-mail je verificiran");
      break;

    case 1019:
      showError("Uneseni OIB već postoji u našoj bazi. Provjerite uneseni broj, a po potrebi nas možete kontaktirati na 091/6089171 ili oglasi@index.hr.");
      break;

    case 1020:
      showError("Uneseni OIB već postoji u našoj bazi. Provjerite uneseni broj, a po potrebi nas možete kontaktirati na 091/6089171 ili oglasi@index.hr.");
      break;

    case 1021:
      showError("Vaš račun je blokiran. Za više informacija nas kontaktirajte na oglasi@index.hr ili 091/6089171.");
      break;

    case 1022:
      showError("Korisnik nije verificiran. Provjerite svoju adresu e-pošte.");
      break;

    case 1023:
      showError("Unijeli ste nedozvoljeni podatak. Kontaktirajte nas na 091/6089171 ili na oglasi@index.hr.")
      break;

    case 1024:
      showError("Unijeli ste nedozvoljene riječi. Promijenite tekst ili nas kontaktirajte na 091/6089171 ili na oglasi@index.hr.")
      break;

    case 1025:
      showError("Nije dozvoljen unos eksternih linkova u oglase. Promijenite unjeti tekst ili nas kontaktirajte na 091/6089171 ili na oglasi@index.hr.")
      break;

    case 1026:
      showError("Naslov oglasa je obvezan.")
      break;

    case 1027:
      showError("Dosegli ste maksimalan broj spremljenih pretraga.")
      break;

    case 1028:
      showError("Ograničenje u broju parametara za sačuvane pretrage je 15.")
      break;

    case 1028:
      showError("Ograničenje u broju parametara za sačuvane pretrage je 15.")
      break;

    case 1040:
      showError("Molimo unesite valjanu lokaciju oglasa.")
      break;

    case 1041:
      showError("Molimo unesite valjanu lokaciju korisnika.")
      break;

    case 1037:
      showError("Nedovoljno kredita")
      break;

    case 1039:
      showError("Oglas nije aktivan")
      break;

    case 1043:
      showError("Oglas već istaknut")
      break;

    case 1045:
      showError("Račun nije pronađen")
      break;

    case 1046:
      showError("Dosegli ste maksimalan broj zahjteva za plaćanje. Ako trebate pomoć, molimo kontaktirajte nas na 091/6089171.")
      break;

    case 1048:
      showError("Korisničko ime ne može započeti ili završiti razmakom.")
      break;

    case 1049:
      showError("Korisničko ime sadrži nedozvoljene znakove.")
      break;

    case 1050:
      showError("Nije dozvoljeno slanje poveznica.")
      break;

    case 1051:
      showError("Nije dozvoljena obnova oglasa koji neće uskoro isteći.")
      break;

    case 1052:
      showError("Prodani oglas se ne može obnoviti.")
      break;

    case 1053:
      showError("Oglas je već prodan.")
      break;

    case 1064:
      showError("Dosegli ste maksimalan broj pokušaja za AI generiranje.")
      break;

    case 1055:
      showError("Možete dodati najviše 5 telefonskih brojeva.")
      break;

    case 1056:
      showError("Ovaj broj je već u upotrebi i ne može se verificirati.")
      break;

    case 1058:
      showError("Možete poslati kod svake 2 minute");
      break;

    case 1059:
      showError("Uneseni kod nije točan. Provjerite poruku i pokušajte ponovno.");
      break;

    case 1062:
      showError("Ovaj broj je već u upotrebi i ne može se verificirati.")
      break;

    case 1063:
      showError("Ovaj broj je blokiran i ne može se koristiti.");
      break;

    case 1065:
      showError("Oglas je već obrisan.");
      break;

    case 1066:
      showError("Dosegnut je maksimalan broj skrivenih oglasa. Za veći limit kontaktirajte nas na 091/6089171 ili oglasi@index.hr");
      break;

    case 1022:
      showError("Za slanje poruka i postavljanje oglasa, vaš profil mora sadržavati osnovne podatke i barem jedan verificirani broj telefona. Želite li sada dopuniti profil?");
      break;
    case 1067:
      showError("Korisnik Vas je blokirao.");
      break;
    case 1070:
      showError("Slanje poruke nije uspjelo – previše zahtjeva u kratkom vremenu.");
      break;
    case 1071:
      showError("Prijava korisnika je moguća samo nakon što razmijenite poruke s njim.");
      break;
    case 1074:
      showError("Razgovor nije pronađen.");
      break;

    case 1075:
      showError("Ocjenu za ovog korisnika već ste ostavili. Svaki oglas možete ocijeniti samo jednom.");
      break;

    case 1076:
      showError("Odgovor na ovu ocjenu je već dan. Na svaku ocjenu može se dati samo jedan odgovor.");
      break;

    case 1077:
      showError("Nije besplatna promocija, korisnik je platio promociju.");
      break;

    case 1079:
      showError("Kako bismo zadržali pouzdan i objektivan sustav ocjenjivanja, istog korisnika nije moguće ocijeniti više puta u kratkom razdoblju.");
      break;

    case 1110:
      showError("Učitavanje slike nije uspjelo, molimo pokušajte ponovno.");
      break;

    case 1111:
      showError("Slika je prevelika.");
      break;

    case 1112:
      showError("Možete učitati samo slikovnu datoteku u formatima JPG, JPEG i PNG.");
      break;

    case 1113:
      showError("Slika je oštećena ili prazna i nije ju moguće učitati.");
      break;

    case 1114:
      showError("Sliku trenutno nije moguće spremiti, molimo pokušajte ponovno.");
      break;

    default:
      showError("Nije moguće povezivanje sa serverom, molimo pokušajte kasnije");
      break;
  }
};

const handleError = async (response: any) => {
  if (response.status === 401) {
    handleUnauthorizedError();
  } else {
    await handleCommonError(response);
  }

  throw response;
};

const handleResponse = async (response: any) => {
  if (response.ok) {
    return response.json();
  }
  throw response;
};

const handleBlobResponse = async (response: any) => {
  if (response.ok) {
    return response.blob();
  }
  throw response;
};

const requests: RequestsI = {
  // eslint-disable-next-line complexity
  get: (
    url,
    params,
    customHandleError,
    apiCacheKey,
    hasProxy = true,
    hasTimeout = true,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);
    resolvedUrl += parseQueryString(params);

    if (hasTimeout && isIE()) {
      resolvedUrl += `${resolvedUrl.includes("?") ? "&" : "?"
        }time=${new Date().getTime()}`;
    }

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        ...defaultOptions(isRemote),
        method: "GET"
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    const isCacheDisable =
      !config.apiCacheTimeout ||
      config.apiCacheTimeout.isNaN ||
      config.apiCacheTimeout <= 0 ||
      !apiCacheKey;

    return isCacheDisable
      ? fetchAPI()
      : getCachedResult(
        `${resolvedUrl}${CACHE_KEY_SEPARATOR}${apiCacheKey}`,
        fetchAPI,
        hasTimeout
      );
  },
  post: (
    url,
    data,
    customHandleError,
    apiCacheKey,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        ...defaultOptions(isRemote),
        method: "POST",
        body: JSON.stringify(data)
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    if (apiCacheKey) {
      removeCache(apiCacheKey);
    }

    return fetchAPI();
  },
  put: (
    url,
    data,
    customHandleError,
    cacheKey,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        ...defaultOptions(isRemote),
        method: "PUT",
        body: JSON.stringify(data)
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    if (cacheKey) {
      removeCache(cacheKey);
    }

    return fetchAPI();
  },
  delete: (
    url,
    customHandleError,
    apiCacheKey,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        ...defaultOptions(isRemote),
        method: "DELETE"
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    if (apiCacheKey) {
      removeCache(apiCacheKey);
    }

    return fetchAPI();
  },
  patch: (
    url,
    data,
    customHandleError,
    cacheKey,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        ...defaultOptions(isRemote),
        method: "PATCH",
        body: JSON.stringify(data)
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    if (cacheKey) {
      removeCache(cacheKey);
    }

    return fetchAPI();
  },
  postFiles: (
    url,
    files,
    customHandleError,
    apiCacheKey,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const fetchAPI = () =>
      fetch(resolvedUrl, {
        method: "POST",
        body: formData,
        credentials: isEdge ? "include" : "same-origin"
      })
        .then(handleResponse)
        .catch(customHandleError || handleError);

    if (apiCacheKey) {
      removeCache(apiCacheKey);
    }

    return fetchAPI();
  },
  getBlob: (
    url,
    customHandleError,
    isRemote = false,
  ) => {
    let resolvedUrl = isRemote ? url : resolveEndpoint(url);

    return fetch(resolvedUrl, {
      ...defaultOptions(isRemote),
      method: "GET"
    })
      .then(handleBlobResponse)
      .catch(customHandleError || handleError);
  }
};

// V2OGLASI-2648: Croatian paucal — pick the right form for a count N:
// 1 → nominative singular ("grad"), 2–4 → paucal ("grada"), else → genitive
// plural ("gradova"). 11–14 are special and take genitive plural.
const croPlural = (n: number, one: string, few: string, many: string) => {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
};

// V2OGLASI-2648: for scope 3/4 ads the card doesn't have room for the full
// comma-separated locationName, so collapse into "<first> + N <plural>".
// Falls back to the deepest known of settlement/city/county/country for
// exact-location ads.
const buildAdCardLocation = (ad: PassedAdI) => {
  const areas = ad.locationAreas;
  if (Array.isArray(areas) && areas.length > 1) {
    const remaining = areas.length - 1;
    const noun = ad.locationScope === 3
      ? croPlural(remaining, "županija", "županije", "županija")
      : croPlural(remaining, "grad", "grada", "gradova");
    return `${areas[0].name} + ${remaining} ${noun}`;
  }
  return ad.locationName || ad.cityName || ad.countyName || ad.countryName || null;
};

// eslint-disable-next-line complexity,max-lines-per-function
export const parseSummaryData = (
  summaryConfig: DetailI,
  ad: PassedAdI,
  favoriteIds: string[],
  summary: string[] = []
) => {
  if (summaryConfig && summary.length === 0) {
    const category = getCategoryConfig(summaryConfig, ad.category);

    if (category) {
      // eslint-disable-next-line complexity
      category.summary.forEach(
        ({ field, type, options, numberFormatted, numberOrdinal, prefix, suffix }) => {
          const data = ad.summary[field];

          if (isAllowedValue(data)) {
            let value: string;

            switch (type) {
              case DetailValueType.singleOption:
                const option = options.find((c: CommonOptionI) => c.value === data);
                // eslint-disable-next-line max-depth
                if (option && option.title) {
                  value = option.title;
                }
                break;

              case DetailValueType.date:
                value = formatDate(data, config.dateFormat, true);
                break;

              case DetailValueType.monthYear:
                value = formatDate(data, config.monthFormat, true);
                break;

              case DetailValueType.year:
                value = formatDate(data, config.yearFormat, true);
                break;

              default:
                value = numberFormatted ? formatNumber(data) : data;
                break;
            }

            let valueString = numberOrdinal ? `${value}.` : value;
            valueString = prefix ? `${prefix} ${valueString}` : valueString;
            valueString = suffix ? `${valueString} ${suffix}` : valueString;
            summary.push(valueString);
          }
        }
      );
    }
  }

  const visitedAds = localStorage.getItem(LocalStorageKey.visitedAd) || [];

  const expireDate = moment(ad.expirationDate).startOf("day");
  const promotionExpireDate = moment(ad.promotionExpirationDate);
  const today = moment().startOf("day");
  const currentTime = moment();
  const remainingDays = expireDate.diff(today, "days");
  const promotionRemainingDays = promotionExpireDate.diff(today, "days");
  const promotionRemainingHours = promotionExpireDate.diff(currentTime, "hours") === 0 ?
    1 : promotionExpireDate.diff(currentTime, "hours");
  // Combine regular images with floor plan images for display in ad list
  const combinedImages = [
    ...(ad.images || []),
    ...(ad.floorPlans || [])
  ];

  return {
    ...ad,
    module: mapModuleToHr(ad.module),
    link: `${ad.smartLink}/${ad.code || ad.id}`,
    category: mapCategoryToHr(ad.category, ad.module),
    expiringSoon:
      (ad.status == 1 || ad.status == 2) && remainingDays <= 9 ? true : false,
    images: combinedImages,
    isFavorite: Boolean(favoriteIds && favoriteIds.includes(ad.id)),
    promotionRemainingDays: ad.isPromoted ? promotionRemainingDays : null,
    promotionRemainingHours: ad.isPromoted ? promotionRemainingHours : null,
    promotionExpirationDate: ad.isPromoted ? ad.promotionExpirationDate : null,
    postedTime: formatDate(ad.postedTime),
    // Location names now come with the ad (city, county or country - deepest known first)
    location: buildAdCardLocation(ad),
    summary,
    isRead: visitedAds.includes(ad.code.toString()),
    categoryLv1Id: ad.categoryLv1Id != emptyUUID ? ad.categoryLv1Id : null,
    categoryLv2Id: ad.categoryLv2Id != emptyUUID ? ad.categoryLv2Id : null,
  }
};

export const Auth = {
  profile: (handleGetError: any) =>
    requests.get(
      `${config.restEndpoint}/user/profile`,
      undefined,
      handleGetError
    ),
  userInfo: (userId: string, params: any, handleGetError: any) =>
    requests.get(
      `${config.restEndpoint}/user/admin-search/${userId}`,
      params,
      handleGetError,
      undefined,
      true
    ),
  autoRenewal: (autoRenewal: boolean, id: string = "") =>
    requests.post(
      `${config.restEndpoint}/user/auto-renewal/${id}`,
      { autoRenewal },
      undefined,
      [ApiCacheKey.user]
    ),
  updateProfile: (profileInfo: any) =>
    requests.put(`${config.restEndpoint}/user`, profileInfo),
  updatePassword: (passwordInfo: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user/change-password`,
      passwordInfo,
      handlePostError
    ),
  getSettings: () => requests.get(`${config.restEndpoint}/user/setting`),
  updateSettings: (settings: any) =>
    requests.put(`${config.restEndpoint}/user/setting`, settings),
  reducedPrices: (data: any) => {
    requests.post(
      `${config.restEndpoint}/user/toggle-reduced-prices`,
      data,
      undefined,
      [ApiCacheKey.user]
    );
  },
};

export const UserFeedback = {
  postUserFeedback: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user-feedback`,
      data,
      handlePostError
    ),
  list: (params: any) =>
    requests.get(`${config.restEndpoint}/user-feedback`, params, undefined),
  update: (data: any) =>
    requests.put(`${config.restEndpoint}/user-feedback`, data)

}

export const Survey = {
  adDeleteReason: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/survey/ad-deleted`,
      data,
      handlePostError
    ),
  aiSearchFeedback: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/survey/ai-search`,
      data,
      handlePostError
    ),
  realEstateFeedback: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/survey/real-estate-prices`,
      data,
      handlePostError
    ),
};
export const AI = {
  suggestedTitle: (data: object) =>
    requests.post(
      `${config.restEndpoint}/suggestion/title?model=claude-3-7-sonnet-latest`,
      data,
      undefined,
      [ApiCacheKey.AI]
    ),
  suggestedDescription: (data: object) =>
    requests.post(
      `${config.restEndpoint}/suggestion/description?model=claude-3-7-sonnet-latest`,
      data,
      undefined,
      [ApiCacheKey.AI]
    ),
};

export const Location = {
  findByCoordinates: (data: any, handleGetError) =>
    requests.post(
      `${config.restEndpoint}/location/find-by-coordinates`,
      data,
      handleGetError
    ),
  fetchAutocompleteOptions: (address: string, handleGetError) =>
    requests.post(
      `${config.restEndpoint}/location/autocomplete`,
      { address },
      handleGetError
    ),
  fetchPlaceDetails: (placeId: string, handleGetError) =>
    requests.post(
      `${config.restEndpoint}/location/place-details`,
      { placeId },
      handleGetError
    ),
}

export const UserAddress = {
  list: () =>
    requests.get(`${config.restEndpoint}/user-address`),
  post: (address: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user-address`,
      address,
      handlePostError
    ),
  delete: (id: string) =>
    requests.delete(`${config.restEndpoint}/user-address/${id}`, undefined),

}

export const Ads = {
  list: (params: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/aditem`,
      mapModulesAndCategories(params),
      handleGetError,
      ApiCacheKey.ad
    ),
  widgetSearch: (params: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/widget-search`,
      mapModulesAndCategories(params),
      handleGetError,
      ApiCacheKey.ad
    ),
  single: (params: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/single-ad`,
      params,
      handleGetError,
      ApiCacheKey.ad
    ),
  updateViewCount: (id: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/update-view-count?id=${id}`
    ),
  reducedPrice: (data: any) => {
    requests.post(
      `${config.restEndpoint}/aditem/toggle-reduced-price`,
      data,
      undefined,
      [ApiCacheKey.ad]
    );
  },
  details: (adId: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/${adId}`,
      undefined,
      handleGetError,
      ApiCacheKey.ad
    ),
  contactDealer: (data: any) =>
    requests.post(
      `${config.restEndpoint}/aditem/send-email-to-seller`,
      data
    ),
  create: (module: string, data: any, handlePostError: any) =>
    requests.post(
      `${config.restEndpoint}/aditem/${mapModuleToEn(module)}`,
      mapModulesAndCategories(data),
      handlePostError,
      [ApiCacheKey.ad]
    ),
  update: (module: string, adId: string, data: any, handlePutError: any) =>
    requests.put(
      `${config.restEndpoint}/aditem/${mapModuleToEn(module)}/${adId}`,
      mapModulesAndCategories(data),
      handlePutError,
      [ApiCacheKey.ad]
    ),
  updatePrice: (adId: string, data: { price: number; priceTo?: number }) =>
    requests.put(
      `${config.restEndpoint}/aditem/price/${adId}`,
      data,
      undefined,
      [ApiCacheKey.ad]
    ),
  myAds: (params: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/my-ads`,
      params,
      undefined,
      ApiCacheKey.ad
    ),
  myAdsStatistic: () =>
    requests.get(
      `${config.restEndpoint}/aditem/my-ads/statistic`,
      undefined,
      undefined,
      ApiCacheKey.ad
    ),
  deleteAds: (adId: string) =>
    requests.delete(`${config.restEndpoint}/aditem/${adId}`, undefined, [
      ApiCacheKey.ad,
      ApiCacheKey.favorite
    ]),
  averagePriceCar: (params: { makeId: string; modelId: string; makeYear: string }) =>
    requests.get(
      `${config.restEndpoint}/aditem/average-price-car`,
      params,
      undefined,
      ApiCacheKey.ad
    ),
  blockUser: (data: any) =>
    requests.post(
      `${config.restEndpoint}/aditem/block-user`,
      data,
      undefined,
      [ApiCacheKey.user]
    ),
  unblockUser: (data: any) =>
    requests.post(
      `${config.restEndpoint}/aditem/unblock-user`,
      data,
      undefined,
      [ApiCacheKey.user]
    ),
  report: (data: any) =>
    requests.post(`${config.restEndpoint}/ad-report`, data),
  manage: (params: any) =>
    requests.get(`${config.restEndpoint}/aditem/manage`, params, undefined),
  duplicate: (params: any) =>
    requests.get(`${config.restEndpoint}/duplicate-ads`, params, undefined),
  duplicates: (duplicateId: string, params: any) =>
    requests.get(
      `${config.restEndpoint}/duplicate-ads/${duplicateId}`,
      params,
      undefined,
      ApiCacheKey.ad
    ),
  flagAsDuplicate: (duplicateId: string, adId: string, isDuplicate: boolean) =>
    requests.put(
      `${config.restEndpoint}/duplicate-ads/${duplicateId}`,
      { adId, isDuplicate },
      undefined,
      [ApiCacheKey.ad]
    ),
  block: (data: any) =>
    requests.post(`${config.restEndpoint}/aditem/block`, data, undefined, [
      ApiCacheKey.ad
    ]),
  unblock: (adId: string) =>
    requests.post(`${config.restEndpoint}/aditem/unblock/${adId}`, undefined, undefined, [
      ApiCacheKey.ad
    ]),
  changeStatus: (id: string, status: number) =>
    requests.post(
      `${config.restEndpoint}/aditem/change-status`,
      { id, status },
      undefined,
      [ApiCacheKey.ad]
    ),
  active: (adId: string) =>
    requests.post(`${config.restEndpoint}/aditem/activate/${adId}`, undefined, undefined, [
      ApiCacheKey.ad
    ]),
  renew: (adId: string) =>
    requests.post(`${config.restEndpoint}/aditem/renew/${adId}`, undefined, undefined, [
      ApiCacheKey.ad
    ]),
  pause: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/pause/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  sold: (adId: string) =>
    requests.post(`${config.restEndpoint}/aditem/sold/${adId}`, undefined, undefined, [
      ApiCacheKey.ad
    ]),
  newAds: (params: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/latest`,
      params,
      undefined,
      ApiCacheKey.ad,
      false
    ),
  popular: (params?: any) =>
    requests.get(
      `${config.restEndpoint}/configuration/populars`,
      params,
      undefined,
      undefined,
      false
    ),
  statistic: () =>
    requests.get(
      `${config.restEndpoint}/aditem/statistic`,
      undefined,
      undefined,
      undefined,
      false
    ),
  adStatistics: (adId: string) =>
    requests.get(
      `${config.restEndpoint}/aditem/statistics/${adId}`,
      undefined,
      undefined,
      ApiCacheKey.ad
    ),
  carouselScroll: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/carousel-scroll/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  similar: (adId: string, quantity: any | number) =>
    requests.get(
      `${config.restEndpoint}/aditem/${adId}/similar?quantity=${quantity}`,
      undefined,
      undefined,
      ApiCacheKey.ad,
      false
    ),
  fetchParamsFromTitle: (title: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/title-props-extraction`,
      { title },
    ),
  fetchRecommended: () =>
    requests.get(`${config.restEndpoint}/suggestion/recommended`),
  seller: (userIdentity: any, handleGetError: any) =>
    requests.get(
      `${config.restEndpoint}/user/${userIdentity}`,
      undefined,
      handleGetError,
      ApiCacheKey.ad
    ),
  count: (params: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/count`,
      params,
      undefined,
      ApiCacheKey.ad,
      false
    ),
  updateShareLink: (adId: string, data: any) =>
    requests.put(
      `${config.restEndpoint}/aditem/${adId}/sharing-configuration`,
      data,
      undefined,
      [ApiCacheKey.ad]
    ),
  make18Old: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/make-18/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  unmake18Old: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/unmake-18/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  priceChanges: (params: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/price-history`,
      params,
      undefined,
      ApiCacheKey.ad
    ),
  statusChanges: (adId: any, params: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/manage/status-changes/${adId}`,
      params,
      undefined,
      ApiCacheKey.ad
    ),
  enableAutoPromotion: (id: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/enable-auto-promotion/${id}`,
      undefined,
      undefined,
      [
        ApiCacheKey.ad,
        ApiCacheKey.user
      ]
    ),
  disableAutoPromotion: (id: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/disable-auto-promotion/${id}`,
      undefined,
      undefined,
      [
        ApiCacheKey.ad,
        ApiCacheKey.user
      ]
    ),
  hideAd: (id: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/hide-ad/${id}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  unhideAd: (id: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/unhide-ad/${id}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  hiddenAds: () =>
    requests.get(
      `${config.restEndpoint}/aditem/hidden-ads`,
      undefined,
      undefined,
      ApiCacheKey.ad
    ),
  sellerPageView: (sellerId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/seller-page/${sellerId}`,
      undefined,
      undefined,
      undefined
    ),
  paymentPromotionStatus: (adId: string) =>
    requests.get(
      `${config.restEndpoint}/aditem/payment-promotion-status/${adId}`,
      undefined,
      undefined,
      undefined
    ),
  fetchCategoryPagePosition: (category: string) =>
    requests.get(
      `${config.restEndpoint}/aditem/page-position/${category}`,
      undefined,
      undefined,
      undefined
    ),
  activateFreePromotion: (adId: string, duration: number) =>
    requests.post(
      `${config.restEndpoint}/aditem/activate-free-promotion`,
      { adId, duration },
      undefined,
      [ApiCacheKey.ad]
    ),
  deactivateFreePromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/deactivate-free-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  activateFreeAutoPromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/activate-free-auto-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  deactivateFreeAutoPromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/deactivate-free-auto-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  activateManualPromotion: (adId: string, duration: number, paymentMethod: number) =>
    requests.post(
      `${config.restEndpoint}/aditem/activate-manual-promotion`,
      { adId, duration, paymentMethod },
      undefined,
      [ApiCacheKey.ad]
    ),
  deactivateManualPromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/deactivate-manual-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  activateManualAutoPromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/activate-manual-auto-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
  deactivateManualAutoPromotion: (adId: string) =>
    requests.post(
      `${config.restEndpoint}/aditem/deactivate-manual-auto-promotion/${adId}`,
      undefined,
      undefined,
      [ApiCacheKey.ad]
    ),
};

export const Favorite = {
  list: (params?: any) =>
    requests.get(
      `${config.restEndpoint}/favorite`,
      params,
      undefined,
      ApiCacheKey.favorite
    ),
  add: (adId: string) =>
    requests.post(`${config.restEndpoint}/favorite/${adId}`, undefined, undefined, [
      ApiCacheKey.favorite,
    ]),
  remove: (adId: string) =>
    requests.delete(`${config.restEndpoint}/favorite/${adId}`, undefined, [
      ApiCacheKey.favorite,
    ]),
  note: (data: any) =>
    requests.post(
      `${config.restEndpoint}/favorite/note/${data.adId}`,
      data,
      undefined,
      [ApiCacheKey.favorite]
    ),
};

// Reveal endpoints have per-call captcha headers and 429 error codes we
// must branch on (1093 → captcha required, 1064 → hard limit). The shared
// `requests.post` helper doesn't accept custom headers and its default
// error handler shows a toast on any non-2xx, so we hit fetch directly.
const CAPTCHA_REQUIRED_CODE = 1093;
const RATE_LIMIT_EXCEEDED_CODE = 1064;

export type RevealResult =
  | { kind: "success"; phones: PhoneInterface[] }
  | { kind: "captchaRequired" }
  | { kind: "rateLimit"; message?: string };

const revealPhones = async (path: string, captchaToken?: string): Promise<RevealResult> => {
  const baseOpts: any = defaultOptions(false);
  const headers: any = {
    ...baseOpts.headers,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (captchaToken) headers["X-Captcha-Token"] = captchaToken;

  const response = await fetch(resolveEndpoint(path), {
    ...baseOpts,
    method: "POST",
    headers,
  });

  if (response.ok) {
    const body = await response.json().catch(() => ({}));
    return { kind: "success", phones: Array.isArray(body && body.phones) ? body.phones : [] };
  }

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    if (body && body.code === CAPTCHA_REQUIRED_CODE) return { kind: "captchaRequired" };
    if (body && body.code === RATE_LIMIT_EXCEEDED_CODE) return { kind: "rateLimit", message: body.message };
  }

  await handleError(response);
  throw response;
};

export const Phone = {
  revealAd: (adId: string, captchaToken?: string) =>
    revealPhones(`${config.restEndpoint}/phone/reveal/ad/${adId}`, captchaToken),
  revealUser: (userId: string, captchaToken?: string) =>
    revealPhones(`${config.restEndpoint}/phone/reveal/user/${userId}`, captchaToken),
  list: (params?: { userId: string }) =>
    requests.get(
      `${config.restEndpoint}/phone/list`,
      params,
      undefined,
      ApiCacheKey.phone
    ),
  single: (id: string) =>
    requests.get(`${config.restEndpoint}/phone/${id}`, undefined, undefined, ApiCacheKey.phone),
  create: (data: PhoneInterface) =>
    requests.post(
      `${config.restEndpoint}/phone`,
      data,
      async (response) => {
        const error = await response.json().catch(() => ({}));
        throw error;
      },
      [ApiCacheKey.phone, ApiCacheKey.ad]
    ),
  update: (id: string, data: PhoneInterface) =>
    requests.put(
      `${config.restEndpoint}/phone/${id}`,
      data,
      undefined,
      [
        ApiCacheKey.phone,
        ApiCacheKey.ad
      ]
    ),
  setPrimary: (id: string) =>
    requests.post(
      `${config.restEndpoint}/phone/set-primary/${id}`,
      undefined,
      undefined,
      [
        ApiCacheKey.phone,
        ApiCacheKey.ad
      ]
    ),
  delete: (id: string) =>
    requests.delete(`${config.restEndpoint}/phone/${id}`, undefined, [
      ApiCacheKey.phone,
      ApiCacheKey.ad
    ]),
  sendPhoneVerificationCode: (id: string) =>
    requests.post(`${config.restEndpoint}/phone/send-phone-verification-code/${id}`, undefined, undefined, [
      ApiCacheKey.phone,
      ApiCacheKey.ad
    ]),
  sendAdminPhoneVerificationEmail: (id: string) =>
    requests.post(`${config.restEndpoint}/phone/verification-request/${id}`, undefined, undefined, [
      ApiCacheKey.phone,
      ApiCacheKey.ad
    ]),
  verifyPhoneVerificationCode: (id: string, code: string) =>
    requests.post(`${config.restEndpoint}/phone/verify-phone-number/${id}`, { code }, undefined, [
      ApiCacheKey.phone,
      ApiCacheKey.ad
    ]),
  getSMSMessageStatus: (id: string) =>
    requests.get(`${config.restEndpoint}/phone/sms-message-status/${id}`),
  phoneVerificationRequestList: (params?: {
    email?: string;
    phoneNumber?: string;
    verificationRequestStatus?: number; // PhoneVerificationRequestStatus enum
    page?: number;
    itemsPerPage?: number;
  }) =>
    requests.get(
      `${config.restEndpoint}/phone/verification-request-search`,
      params,
      undefined,
      ApiCacheKey.phone
    ),
  phoneVerificationRequestUpdate: (id: string, data: {
    verificationRequestStatus: number; // PhoneVerificationRequestStatus enum
    adminNote?: string;
  }) =>
    requests.put(
      `${config.restEndpoint}/phone/verification-request/${id}`,
      data,
      undefined,
      [ApiCacheKey.phone]
    ),
};

export const Promotion = {
  list: (params?: any) =>
    requests.get(
      `${config.restEndpoint}/promotion`,
      params,
      undefined,
      ApiCacheKey.promotion
    ),
};

// Error codes whose toast should be suppressed — the UI rendering the request
// already conveys the error in context (e.g. empty state), so a toast would
// be noise. Add a new code here with a comment when a screen needs this.
export const SILENT_ERROR_CODES = [
  1082, // /statistics/real-estate-price — not enough data points for selection
  1083, // /statistics/real-estate-price — no data for selected location
];

// Shared error handler that swallows SILENT_ERROR_CODES (no toast) and
// delegates everything else to the default code-to-toast mapping.
export const handleSilentError = async (response: any) => {
  let body: any = {};
  try { body = await response.json(); } catch (e) { /* ignore parse error */ }

  if (SILENT_ERROR_CODES.indexOf(body.code) !== -1) return null;
  if (body.code) showErrorByCode(body.code);
  return null;
};

export const Statistics = {
  promotion: () => requests.get(`${config.restEndpoint}/statistics/promotion`),
  clients: (params?: any) => requests.get(`${config.restEndpoint}/statistics/clients`, params),
  sales: (params?: any) => requests.get(`${config.restEndpoint}/statistics/sales`, params),
  moduleSpending: (params?: any) => requests.get(`${config.restEndpoint}/statistics/module-spending`, params),
  promotedViews: (params?: any) => requests.get(`${config.restEndpoint}/statistics/promoted-views`, params),
  calculateScrapedUsersStatistics: (data: any) =>
    requests.post(
      `${config.restEndpoint}/statistics/calculate-scraped-users-statistics`,
      data
    ),
  getScrapedUsersStatistics: () =>
    requests.get(
      `${config.restEndpoint}/statistics/get-scraped-users-statistics`,
      undefined,
      async (response: any) => { throw response; }
    ),
  overview: () => requests.get(`${config.restEndpoint}/statistics/overview`),
  startFile: (data: any) => requests.post(`${config.restEndpoint}/statistics/files`, data),
  file: (id: string) => requests.get(`${config.restEndpoint}/statistics/files/${id}`),
  downloadFile: (id: string) => requests.getBlob(`${config.restEndpoint}/statistics/files/${id}/download`),
  realEstatePrice: (params: any) =>
    requests.get(
      `${config.restEndpoint}/statistics/real-estate-price`,
      params,
      handleSilentError
    ),
};

export const AdminDashboard = {
  overview: (params: any) => requests.get(`${config.restEndpoint}/admin-dashboard/overview`, params),
  supply: (params: any) => requests.get(`${config.restEndpoint}/admin-dashboard/supply`, params),
  demand: (params: any) => requests.get(`${config.restEndpoint}/admin-dashboard/demand`, params),
  revenue: (params: any) => requests.get(`${config.restEndpoint}/admin-dashboard/revenue`, params),
  operations: (params: any) => requests.get(`${config.restEndpoint}/admin-dashboard/operations`, params),
};

export const Credit = {
  list: (params?: any) =>
    requests.get(
      `${config.restEndpoint}/store-credit`,
      params,
      undefined,
      ApiCacheKey.credits
    ),
  add: (id: string, data: any) =>
    requests.post(
      `${config.restEndpoint}/store-credit/${id}`,
      data,
      undefined,
      [
        ApiCacheKey.credits,
        ApiCacheKey.user
      ]
    ),
  log: (params: any) =>
    requests.get(
      `${config.restEndpoint}/store-credit/transaction-log`,
      params,
      undefined,
      ApiCacheKey.credits
    ),
};

export const Cart = {
  list: (params: any) =>
    requests.get(
      `${config.restEndpoint}/shopping-cart`,
      params,
      undefined,
      ApiCacheKey.cart
    ),
  myList: (params: any) =>
    requests.get(
      `${config.restEndpoint}/shopping-cart/my-shopping-carts`,
      params,
      undefined,
      ApiCacheKey.cart
    ),
  single: (id: string) =>
    requests.get(`${config.restEndpoint}/shopping-cart/${id}`, undefined, undefined, ApiCacheKey.cart),
  create: (data: any) =>
    requests.post(`${config.restEndpoint}/shopping-cart`, data, undefined, [
      ApiCacheKey.cart,
      ApiCacheKey.ad,
      ApiCacheKey.user
    ]),
  update: (id: string, data: any) =>
    requests.put(
      `${config.restEndpoint}/shopping-cart/${id}`,
      data,
      undefined,
      [
        ApiCacheKey.cart,
        ApiCacheKey.ad,
      ]
    ),
  invoice: (id: string) =>
    requests.get(`${config.restEndpoint}/shopping-cart/invoice/${id}`,
      undefined,
      undefined,
      ApiCacheKey.cart
    ),
  bankTransferInfo: (id: string) =>
    requests.get(`${config.restEndpoint}/shopping-cart/bank-transfer-data/${id}`,
      undefined,
      undefined,
      ApiCacheKey.cart
    ),
  delete: (id: string) =>
    requests.delete(`${config.restEndpoint}/shopping-cart/${id}`, undefined, [
      ApiCacheKey.cart,
      ApiCacheKey.ad
    ]),
};

export const SavedSearch = {
  get: (params: any) =>
    requests.get(
      `${config.restEndpoint}/saved-search`,
      params,
      undefined,
      ApiCacheKey.savedSearch
    ),
  create: (data: any) =>
    requests.post(`${config.restEndpoint}/saved-search`, data, undefined, [
      ApiCacheKey.savedSearch
    ]),
  update: (savedSearchId: any, data: any) =>
    requests.put(
      `${config.restEndpoint}/saved-search/${savedSearchId}`,
      data,
      undefined,
      [ApiCacheKey.savedSearch]
    ),
  delete: (savedSearchId: any) =>
    requests.delete(
      `${config.restEndpoint}/saved-search/${savedSearchId}`,
      undefined,
      [ApiCacheKey.savedSearch]
    )
};

export const User = {
  list: (params: any) =>
    requests.get(
      `${config.restEndpoint}/user`,
      params,
      undefined,
      ApiCacheKey.user
    ),
  checkIdentityValue: (identityValue: any) =>
    requests.get(
      `${config.restEndpoint}/user/check-duplicate-identity-value/${identityValue}`,
      undefined,
      undefined,
      [ApiCacheKey.user]
    ),
  checkUsernameAvailability: (params: any) =>
    requests.post(
      `${config.restEndpoint}/user/check-username-availability`,
      params,
      undefined,
      [ApiCacheKey.user]
    ),
  block: (userId: string, blockReasonId: number, note: string, blockingDecisionBasedOn: number) =>
    requests.post(
      `${config.restEndpoint}/user/block`,
      { userId, blockReasonId, note: note || null, blockingDecisionBasedOn },
      undefined,
      [ApiCacheKey.user]
    ),
  getBlockReasons: () =>
    requests.get(`${config.restEndpoint}/user/block-reasons`),
  unblock: (userId: string) =>
    requests.post(
      `${config.restEndpoint}/user/unblock`,
      { userId },
      undefined,
      [ApiCacheKey.user]
    ),
  triggerShadowBlock: (userId: string, isBanned: boolean) =>
    requests.post(
      `${config.restEndpoint}/user/set-chat-shadow-ban`,
      { userId, isBanned },
      undefined,
      [ApiCacheKey.user]
    ),
  createAdmin: (data: any) =>
    requests.post(`${config.restEndpoint}/user/create-admin`, data, undefined, [
      ApiCacheKey.user
    ]),
  createMember: (data: any, handlePostError?: any) =>
    requests.post(`${config.restEndpoint}/user/create-member`, data, handlePostError, [
      ApiCacheKey.user
    ]),
  statistic: () =>
    requests.get(
      `${config.restEndpoint}/user/statistic`,
      undefined,
      undefined,
      undefined,
      false
    ),
  userStatistic: (id: string) =>
    requests.get(`${config.restEndpoint}/user/statistic/${id}`),
  userAdsStatistics: (userId: string, params: { startDate?: string; endDate?: string } = {}) =>
    requests.get(`${config.restEndpoint}/aditem/user-ads-statistics/${userId}`, params),
  email: (id: string, data: any) =>
    requests.post(`${config.restEndpoint}/user/${id}/email`, data),
  deleteAsync: (id: string) =>
    requests.delete(
      `${config.restEndpoint}/user/${id}`,
      undefined,
      [ApiCacheKey.user]
    ),
  updateProfile: (userId: string, profileInfo: any) =>
    requests.put(
      `${config.restEndpoint}/user/${userId}`,
      profileInfo,
      undefined,
      [ApiCacheKey.user]
    ),
  sendActivationEmail: (userId: string) =>
    requests.post(`${config.restEndpoint}/user/send-activation-email/${userId}`),
  activateXmlSync: (userId: string, activateXmlSync: boolean) =>
    requests.post(
      `${config.restEndpoint}/user/toggle-xml-sync`,
      {
        userId,
        activateXmlSync
      },
      undefined,
      [ApiCacheKey.user]
    ),
  toggleTwoFactor: (userId: string, twoFactorEnabled: boolean) =>
    requests.post(
      `${config.restEndpoint}/user/toggle-twofactor`,
      {
        userId,
        twoFactorEnabled
      },
      undefined,
      [ApiCacheKey.user]
    ),
  assignDominantCategories: (
    userId: string,
    dominantCategories: { module: string; category: string }[],
    handlePutError?: any
  ) =>
    requests.put(
      `${config.restEndpoint}/user/assign-dominant-categories`,
      {
        userId,
        dominantCategories: (dominantCategories || []).map(dc => ({
          module: mapModuleToEn(dc.module),
          category: mapCategoryToEn(dc.category, dc.module)
        }))
      },
      handlePutError,
      [ApiCacheKey.user]
    )
};

export const AdminNotes = {
  list: (params: any) =>
    requests.get(
      `${config.restEndpoint}/admin-note/${params.entityId}`,
      params,
      undefined,
      ApiCacheKey.note
    ),
  create: (data: any) =>
    requests.post(`${config.restEndpoint}/admin-note`, data, undefined, [
      ApiCacheKey.note
    ]),
  update: (id: string, data: any) =>
    requests.put(`${config.restEndpoint}/admin-note/${id}`, data, undefined, [
      ApiCacheKey.note
    ]),
  delete: (id: string) =>
    requests.delete(`${config.restEndpoint}/admin-note/${id}`, undefined, [
      ApiCacheKey.note
    ])
};

export const Routes = {
  list: (params: any) =>
    requests.get(
      `${config.restEndpoint}/routes`,
      params,
      undefined,
      ApiCacheKey.routes
    )
}

export const Category = {
  list: () =>
    requests.get(
      `${config.restEndpoint}/configuration/categories`,
      undefined,
      undefined,
      ApiCacheKey.category,
      false,
      false
    ),
  fetchPreviouslySelectedCategories: () => requests.get(
    `${config.restEndpoint}/suggestion/category-suggestions`
  ),
  fetchSuggestedCategories: (params: any) => requests.get(
    `${config.restEndpoint}/suggestion/category`,
    params
  ),
  confirmSuggestedCategory: (params: any) => requests.post(
    `${config.restEndpoint}/suggestion/category/confirm`,
    params
  )
};

export const Module = {
  list: () =>
    requests.get(
      `${config.restEndpoint}/configuration/modules`,
      undefined,
      undefined,
      ApiCacheKey.module,
      false,
      false
    ),
  // Names of the modules in which the seller has active ads
  sellerModules: (sellerId: string) =>
    requests.get(
      `${config.restEndpoint}/configuration/modules/seller/${sellerId}`,
      undefined,
      undefined,
      ApiCacheKey.module,
      false,
      false
    ),
  active: (id: string) =>
    requests.post(`${config.restEndpoint}/module/active`, { id }, undefined, [
      ApiCacheKey.module
    ]),
  inActive: (id: string) =>
    requests.post(`${config.restEndpoint}/module/inActive`, { id }, undefined, [
      ApiCacheKey.module
    ]),
  create: (data: any) =>
    requests.post(`${config.restEndpoint}/module`, data, undefined, [
      ApiCacheKey.module
    ]),
  edit: (data: any, id: string) =>
    requests.put(`${config.restEndpoint}/module/${id}`, data, undefined, [
      ApiCacheKey.module
    ]),
  updateOrder: (data: any) =>
    requests.put(
      `${config.restEndpoint}/module/update-order`,
      data,
      undefined,
      [ApiCacheKey.module]
    ),
  manage: () => requests.get(`${config.restEndpoint}/module/manage`),
  make18Old: (categoryId: string) =>
    requests.post(
      `${config.restEndpoint}/module/make-18/${categoryId}`,
      undefined,
      undefined,
      [ApiCacheKey.module]
    ),
  unmake18Old: (categoryId: string) =>
    requests.post(
      `${config.restEndpoint}/module/unmake-18/${categoryId}`,
      undefined,
      undefined,
      [ApiCacheKey.module]
    )
};

export const Report = {
  list: (params: any) =>
    requests.get(`${config.restEndpoint}/ad-report`, params, undefined),
  update: (id: string, data: any) =>
    requests.put(`${config.restEndpoint}/ad-report/${id}`, data)
};

export const ChatReport = {
  list: (params: any) =>
    requests.get(`${config.restEndpoint}/chat-report`, params, undefined),
  update: (id: string, data: any) =>
    requests.put(`${config.restEndpoint}/chat-report/${id}`, data),
  report: (data: any) =>
    requests.post(`${config.restEndpoint}/chat-report`, data),
};

// Scoped configuration datasets. Replaces configuration/datasource/:name which
// returned whole documents (all makes, the full location tree, ...).
// Cached under ApiCacheKey.config with the standard timeout, so an open tab picks up
// makes/locations an admin changed within a minute; admin writes clear the key at once.
const configurationGet = (path: string, params?: any, customHandleError?: any) =>
  requests.get(
    `${config.restEndpoint}/configuration/${path}`,
    params,
    customHandleError,
    [ApiCacheKey.config],
    false
  );

export const Configuration = {
  // Makes of one category (car, motorbike, trailers, buses, agricultural-machines) with models
  makes: (category: string, customHandleError?: any) =>
    configurationGet("makes", { category }, customHandleError),
  // lv1/lv2 subcategory tree of one module/category (names, not routes)
  subcategories: (module: string, category: string, customHandleError?: any) =>
    configurationGet("subcategories", { module, category }, customHandleError),
  countries: () => configurationGet("countries"),
  // Popular locations followed by the "Cijela Hrvatska" / "Izvan Hrvatske" filters
  popularLocations: () => configurationGet("locations/popular"),
  location: (id: string, customHandleError?: any) =>
    configurationGet(`locations/${id}`, undefined, customHandleError),
  locationChildren: (id: string, customHandleError?: any) =>
    configurationGet(`locations/${id}/children`, undefined, customHandleError),
  // Flat search over Croatian counties, cities and settlements (min 2
  // characters). Pass level=1 to limit to counties, level=2 to cities.
  searchLocations: (query: string, level?: number) =>
    configurationGet("locations/search", level ? { query, level } : { query }),
  // { county, city?, settlement? } slugs or { id } -> location with ancestor ids, names and routes
  resolveLocation: (
    params: { id?: string; county?: string; city?: string; settlement?: string },
    customHandleError?: any
  ) => configurationGet("locations/resolve", params, customHandleError)
};

export const Config = {
  UI: (fileName: any) =>
    requests.get(
      `${config.uiConfigEndpoint}/hr/${fileName}`,
      { v: process.env.DEPLOYMENT_TIMESTAMP },
      undefined,
      [ApiCacheKey.resource],
      false,
      false
    ),
};

export const Search = {
  suggestion: (params: any) =>
    requests.get(
      `${config.restEndpoint}/suggestion`,
      params,
      undefined,
      undefined,
      false
    ),
  // In previous version there were 5 params: url, null, undefined, undefined, false
  updateWeight: (id: string) =>
    requests.put(
      `${config.restEndpoint}/suggestion/${id}/weight`,
      null,
      undefined,
      undefined
    ),
  suggestionAlternative: (params: any) =>
    requests.get(
      `${config.restEndpoint}/suggestion/alternative`,
      params,
      undefined,
      undefined,
      false
    )
};

export const AiSearch = {
  query: (userQuery: string) => {
    const model = getCookie('ai_model');
    return requests.get(
      '/api/ai-search/query',
      { userQuery: encodeURIComponent(userQuery), ...(model && { model }) },
      undefined,
      undefined,
      false
    );
  },
  mostSearched: (text: string, handleGetError?: any) =>
    requests.get(
      '/api/suggestion/most-searched',
      { text: encodeURIComponent(text) },
      handleGetError,
      undefined,
      false
    ),
};

export const Agency = {
  list: (params: any) =>
    requests.get(
      `${config.restEndpoint}/agent`,
      params,
      undefined,
      ApiCacheKey.agency
    ),
  create: (data: any) =>
    requests.post(`${config.restEndpoint}/agent`, data, undefined, [
      ApiCacheKey.agency
    ]),
  update: (agencyId: any, data: any) =>
    requests.put(`${config.restEndpoint}/agent/${agencyId}`, data, undefined, [
      ApiCacheKey.agency
    ]),
  delete: (agencyId: any) =>
    requests.delete(`${config.restEndpoint}/agent/${agencyId}`, undefined, [
      ApiCacheKey.agency
    ]),
  generateFeedSecret: (agencyId: any) =>
    requests.post(
      `${config.restEndpoint}/agent/${agencyId}/feed-secret`,
      {},
      undefined,
      [ApiCacheKey.agency]
    ),
  revokeFeedSecret: (agencyId: any) =>
    requests.delete(
      `${config.restEndpoint}/agent/${agencyId}/feed-secret`,
      undefined,
      [ApiCacheKey.agency]
    )
};

export const Chat = {
  getChats: (params: getChatsRequest, handleGetError?: any): Promise<getChatsListResponse> =>
    requests.get(
      `${config.restEndpoint}/chat/list`,
      params,
      handleGetError
    ),
  getChat: (params: getChatRequest, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/chat`,
      params,
      handleGetError
    ),
  sendMessage: (data: sendMessageRequest, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/chat/message`,
      data,
      handlePostError
    ),
  markAsRead: (chatId: string, messageId: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/chat/${chatId}/read-message/${messageId}`,
      undefined,
      handlePostError
    ),
  getUnreadCount: (handleGetError?: any): Promise<unreadCountResponse> =>
    requests.get(
      `${config.restEndpoint}/chat/unread-count`,
      undefined,
      handleGetError,
    ),
  blockUser: (userId: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user/block/${userId}`,
      undefined,
      handlePostError
    ),
  unblockUser: (userId: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user/unblock/${userId}`,
      undefined,
      handlePostError
    ),
  deleteChat: (chatIds: string[], handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/chat/archive`,
      { chatIds },
      handlePostError
    ),
  reportUser: (data, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/chat-report`,
      { ...data, reason: CHAT_REPORT_REASON_ENUM },
      handlePostError
    ),
  getFilterInfo: (handlePostError?: any) =>
    requests.get(
      `${config.restEndpoint}/chat/filter-data`,
      undefined,
      handlePostError
    ),
  getChatLog: (chatId: string, handlePostError?: any) =>
    requests.get(
      `${config.restEndpoint}/chat/log?chatId=${chatId}`,
      undefined,
      handlePostError
    ),
  uploadDocuments: (files: File[], handlePostError?: any) =>
    requests.postFiles(
      `${config.restEndpoint}/chat-document`,
      files,
      handlePostError
    ),
  getDocumentBlob: async (fileName: string): Promise<string> => {
    const blob = await requests.getBlob(
      `${config.restEndpoint}/chat-document/${fileName}`
    );
    return URL.createObjectURL(blob);
  }
};

export const Utils = {
  getVimeoVideoThumbnail: (videoId: string, handleGetError: any) =>
    requests.get(
      `https://vimeo.com/api/v2/video/${videoId}.json`,
      undefined,
      handleGetError,
      ApiCacheKey.utils,
      true,
      true,
      true
    )
};

export const UserRating = {
  getRatingEligibilityAds: (userId: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/aditem/rating-eligibility-ads/${userId}`,
      undefined,
      handleGetError,
      ApiCacheKey.rating
    ),
  create: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user-rating`,
      data,
      handlePostError,
      [ApiCacheKey.rating]
    ),
  answer: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/user-rating/answer`,
      data,
      handlePostError,
      [ApiCacheKey.rating]
    ),
  list: (params: {
    userId: string;
    type?: number;
    page?: number;
    itemPerPage?: number;
  }, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/user-rating`,
      params,
      handleGetError,
      ApiCacheKey.rating
    ),
  getOverallCount: (userId: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/user-rating/overall-count/${userId}`,
      undefined,
      handleGetError,
      ApiCacheKey.rating
    ),
  adminSearch: (params: {
    code?: string;
    sellerEmail?: string;
    buyerEmail?: string;
    status?: number;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    itemPerPage?: number;
  }, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/user-rating/admin-search`,
      params,
      handleGetError,
      ApiCacheKey.rating
    ),
  setStatus: (userRatingId: string, data: { status: number }, handlePutError?: any) =>
    requests.put(
      `${config.restEndpoint}/user-rating/set-status/${userRatingId}`,
      data,
      handlePutError,
      [ApiCacheKey.rating]
    ),
  pageAccessed: () =>
    requests.post(
      `${config.restEndpoint}/user-rating/page-accessed`,
      {}
    )
};

export const SystemMessage = {
  getMessages: (params?: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/system-messages`,
      params,
      handleGetError
    ),
  getMessage: (id: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/system-messages/${id}`,
      undefined,
      handleGetError
    ),
  getUnreadCount: (handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/system-messages/unread-count`,
      undefined,
      handleGetError
    ),
  deleteMessages: (ids: string[]) =>
    requests.post(
      `${config.restEndpoint}/system-messages/delete`,
      { systemMessageIds: ids }
    ),
  send: (data, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/system-messages/send`,
      data,
      handlePostError
    ),
  setPromptChoice: (id: string, promptChoice: boolean, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/system-messages/set-prompt-choice/${id}`,
      { promptChoice },
      handlePostError
    ),
  getAdminMessages: (params?: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/system-messages/admin-list`,
      params,
      handleGetError
    ),
};

export const Image = {
  upload: (files: File[], handlePostError?: any) =>
    requests.postFiles(
      config.imageUploadEndpoint,
      files,
      handlePostError
    ),
};

export const FeaturedStores = {
  list: (module?: string, category?: string, page: number = 1, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/featured-store`,
      {
        module: module ? mapModuleToEn(module) : module,
        category: category ? mapCategoryToEn(category, module) : category,
        page
      },
      handleGetError
    ),
  slot: (module?: string, category?: string, locationId?: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/featured-store/slot`,
      {
        module: module ? mapModuleToEn(module) : module,
        category: category ? mapCategoryToEn(category, module) : category,
        locationId
      },
      handleGetError
    ),
  subscribe: (userId: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/featured-store/subscribe`,
      { userId },
      handlePostError,
      [ApiCacheKey.user]
    ),
  unsubscribe: (storeId: string, handleDeleteError?: any) =>
    requests.delete(
      `${config.restEndpoint}/featured-store/unsubscribe/${storeId}`,
      handleDeleteError,
      [ApiCacheKey.user]
    ),
};

export const MakeModelInfo = {
  getByMakeModel: (makeId: string, modelId: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/infopage`,
      { makeId, modelId },
      handleGetError,
      ApiCacheKey.makeModelInfo
    ),
  list: (params?: any, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/infopage/list`,
      params,
      handleGetError,
      ApiCacheKey.makeModelInfo
    ),
  getById: (id: string, handleGetError?: any) =>
    requests.get(
      `${config.restEndpoint}/infopage/${id}`,
      undefined,
      handleGetError,
      ApiCacheKey.makeModelInfo
    ),
  save: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/infopage`,
      data,
      handlePostError,
      [ApiCacheKey.makeModelInfo]
    ),
  publish: (id: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/infopage/publish/${id}`,
      undefined,
      handlePostError,
      [ApiCacheKey.makeModelInfo]
    ),
};

export const Scrape = {
  createJob: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/scrape/jobs`,
      data,
      handlePostError
    ),
  jobs: (params: any) =>
    requests.get(
      `${config.restEndpoint}/scrape/jobs`,
      params,
      async (response: any) => { throw response; }
    ),
  cancelJob: (id: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/scrape/jobs/${id}/cancel`,
      {},
      handlePostError
    ),
  rerunJob: (id: string, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/scrape/jobs/${id}/rerun`,
      {},
      handlePostError
    ),
  deleteJob: (id: string, handleError?: any) =>
    requests.delete(
      `${config.restEndpoint}/scrape/jobs/${id}`,
      handleError
    ),
  downloadViewComparison: (id: string) =>
    requests.getBlob(
      `${config.restEndpoint}/scrape/jobs/${id}/download`,
      async (response: any) => { throw response; }
    ),
};

export const AdminConfiguration = {
  makeCategories: () =>
    requests.get(
      `${config.restEndpoint}/configuration/admin/makes/categories`,
      undefined,
      async (response: any) => { throw response; }
    ),
  makes: (params: any) =>
    requests.get(
      `${config.restEndpoint}/configuration/admin/makes`,
      params,
      async (response: any) => { throw response; }
    ),
  createMake: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/configuration/admin/makes`,
      data,
      handlePostError,
      [ApiCacheKey.config]
    ),
  updateMake: (id: string, data: any, handlePostError?: any) =>
    requests.put(
      `${config.restEndpoint}/configuration/admin/makes/${id}`,
      data,
      handlePostError,
      [ApiCacheKey.config]
    ),
  // reloads the makes from Mongo on every instance, after rows were inserted or changed directly in the database
  refreshMakes: (handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/configuration/admin/makes/refresh`,
      undefined,
      handlePostError,
      [ApiCacheKey.config]
    ),
  locations: (params: any) =>
    requests.get(
      `${config.restEndpoint}/configuration/admin/locations`,
      params,
      async (response: any) => { throw response; }
    ),
  createLocation: (data: any, handlePostError?: any) =>
    requests.post(
      `${config.restEndpoint}/configuration/admin/locations`,
      data,
      handlePostError,
      [ApiCacheKey.config]
    ),
  updateLocation: (id: string, data: any, handlePostError?: any) =>
    requests.put(
      `${config.restEndpoint}/configuration/admin/locations/${id}`,
      data,
      handlePostError,
      [ApiCacheKey.config]
    ),
};



// WEBPACK FOOTER //
// ./src/services/api.ts